import {
  SKIP,
  visit
} from "../../../chunk-NEG2FAMT.mjs";
import "../../../chunk-D57E6H3M.mjs";

// src/components/composite/ChatMarkdown/streamingAnimation.ts
import * as React from "react";
import { PRODUCT_MENTION_LABEL } from "../../../domain/index.mjs";
var STREAM_REVEAL_MS = 420;
var STREAM_STAGGER_MS = 10;
var STREAM_MAX_STAGGER_MS = 240;
var STREAM_REVEAL_HOLD_MS = 280;
var STREAM_SETTLE_MS = STREAM_REVEAL_MS + STREAM_MAX_STAGGER_MS + STREAM_REVEAL_HOLD_MS;
var NOT_STREAMED = { age: Number.POSITIVE_INFINITY, start: Number.NEGATIVE_INFINITY };
function useStreamingAge(animateState, indexKey) {
  const [initial] = React.useState(() => {
    if (!animateState) return NOT_STREAMED;
    const now = Date.now();
    let st = animateState.charStartTimes.get(indexKey);
    if (!st) {
      st = Math.min(Math.max(now, (animateState.lastStartTime ?? 0) + STREAM_STAGGER_MS), now + STREAM_MAX_STAGGER_MS);
      animateState.lastStartTime = st;
      animateState.charStartTimes.set(indexKey, st);
    }
    return { age: now - st, start: st };
  });
  return initial;
}
function streamRevealProps(age) {
  if (age >= STREAM_REVEAL_MS) return {};
  return { className: "buyscout-char-fade-in", style: age !== 0 ? { animationDelay: `${-age}ms` } : void 0 };
}
var JOINING_CODE_POINT = /[\u200D\u0E33\u0EB3\u1100-\u11FF\uA960-\uA97F\uD7B0-\uD7FF\u{1F1E6}-\u{1F1FF}\u{E0020}-\u{E007F}\p{M}]/u;
var SKIN_TONE = /[\u{1F3FB}-\u{1F3FF}]/u;
var graphemeSegmenter = typeof Intl !== "undefined" && typeof Intl.Segmenter === "function" ? new Intl.Segmenter(void 0, { granularity: "grapheme" }) : null;
function visibleCharacters(text) {
  if (!graphemeSegmenter || !(JOINING_CODE_POINT.test(text) || SKIN_TONE.test(text))) return Array.from(text);
  return Array.from(graphemeSegmenter.segment(text), (part) => part.segment);
}
function rehypeCharacterStream() {
  return (tree) => {
    let fallbackIndex = 0;
    visit(tree, "text", (node, index, parent) => {
      if (!parent || parent.tagName === "code") return;
      const textValue = node.value || "";
      if (/^\s*$/.test(textValue) && textValue.includes("\n")) return;
      const chars = visibleCharacters(textValue);
      let currentOffset = node.position?.start?.offset;
      const newNodes = chars.map((char) => {
        const charIndex = typeof currentOffset === "number" ? currentOffset : fallbackIndex++;
        if (typeof currentOffset === "number") currentOffset += char.length;
        return {
          type: "element",
          tagName: "stream-char",
          properties: { charIndex },
          children: [{ type: "text", value: char }]
        };
      });
      if (typeof index !== "number") return;
      parent.children?.splice(index, 1, ...newNodes);
      return [SKIP, index + newNodes.length];
    });
  };
}
function closeOpenMarker(text, marker) {
  const trailingMatch = text.match(/(\s+)$/);
  if (trailingMatch) {
    const ws = trailingMatch[1];
    let base = text.slice(0, -ws.length);
    if (base.endsWith(marker)) base += "\u200B";
    return `${base}${marker}${ws}`;
  }
  if (text.endsWith(marker)) return `${text}\u200B${marker}`;
  return `${text}${marker}`;
}
var INCOMPLETE_MENTION_SUFFIX = new RegExp(String.raw`@\[(${PRODUCT_MENTION_LABEL})\]\([^)]*$`);
function preprocessStreamingContent(content) {
  let processed = content;
  processed = processed.replace(/@\[[^\]]*$/, "");
  processed = processed.replace(/@\[(?:[^[\]\n]|(?<!@)\[[^[\]\n]*\](?!\())*$/, "");
  processed = processed.replace(INCOMPLETE_MENTION_SUFFIX, "@[$1]");
  processed = processed.replace(/(?<!@)!?\[[^\]]*\]\([^)]*$/, "");
  processed = processed.replace(/(?<!@)!?\[[^\]]*\]$/, "");
  processed = processed.replace(/(?<!@)!?\[[^\]\n]*$/, "");
  const codeBlockMatches = processed.match(/```/g) || [];
  if (codeBlockMatches.length % 2 !== 0) {
    processed += "\n```";
  } else {
    const doubleAsterisks = (processed.match(/\*\*/g) || []).length;
    if (doubleAsterisks % 2 !== 0) processed = closeOpenMarker(processed, "**");
    const singleAsterisks = (processed.replace(/\*\*/g, "").match(/\*/g) || []).length;
    if (singleAsterisks % 2 !== 0) processed = closeOpenMarker(processed, "*");
    const inlineBackticks = (processed.replace(/```/g, "").match(/`/g) || []).length;
    if (inlineBackticks % 2 !== 0) processed += "`";
  }
  return processed;
}
export {
  STREAM_REVEAL_HOLD_MS,
  STREAM_REVEAL_MS,
  STREAM_SETTLE_MS,
  preprocessStreamingContent,
  rehypeCharacterStream,
  streamRevealProps,
  useStreamingAge
};
//# sourceMappingURL=streamingAnimation.mjs.map